import { useCallback, useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Loader from '../../components/atoms/Loader';
import DialogModal from '../../components/molecules/DialogModal';
import RequestFilterBar from '../../components/organisms/RequestFilterBar';
import RequestsTable from '../../components/organisms/RequestsTable';
import JobSummaryTable from '../../components/organisms/JobSummaryTable';
import StatusDonutChart from '../../components/charts/StatusDonutChart';
import { filterFields, REQUEST_ROWS_PER_PAGE_OPTIONS } from '../../constants';
import {
  fetchAllHealthSystems,
  fetchAllPurposeOfUses,
  fetchAllRequests,
  fetchHealthSystemSummaryCount,
} from '../../services/DashboardService';
import { validateDateRange } from '../../utils/dateRange';
import {
  isNumberIn,
  isString,
  usePersistedState,
} from '../../utils/persistedState';
import type {
  FilterField,
  HealthSystemOption,
  JobSummaryRow,
  Option,
  PurposeOfUseOption,
  RequestRow,
  StatusCount,
} from '../../interfaces/Dashboard';
import { fetchJobSummary } from '../../services/DashboardService';

const DEFAULT_ROWS_PER_PAGE = 100;
const DEFAULT_JOB_SUMMARY_ROWS_PER_PAGE = 5;

/** Fresh copy of the search-field config with purpose-of-use options injected. */
const buildSearchFields = (
  purposeOfUses: PurposeOfUseOption[],
): FilterField[] =>
  filterFields.map((field) =>
    field.responseValue === 'purposeofuse'
      ? {
          ...field,
          options: purposeOfUses,
          value: purposeOfUses[0] ?? null,
        }
      : { ...field, value: field.type === 'select' ? field.value : '' },
  );

/**
 * Dashboard: per-health-system status donut plus a searchable, server-paginated
 * request grid with a job-summary detail modal.
 */
const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isGridLoading, setIsGridLoading] = useState(false);

  const [healthSystems, setHealthSystems] = useState<HealthSystemOption[]>([]);
  const [selectedHealthSystem, setSelectedHealthSystemState] =
    useState<HealthSystemOption | null>(null);
  const [statusCounts, setStatusCounts] = useState<StatusCount[]>([]);

  const [searchFields, setSearchFields] = useState<FilterField[]>(filterFields);
  const [purposeOfUses, setPurposeOfUses] = useState<PurposeOfUseOption[]>([]);

  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [totalRequestCount, setTotalRequestCount] = useState<number | null>(
    null,
  );
  const [page, setPage] = useState(0);
  // Health systems load asynchronously, so only the selected *value* is
  // persisted (validated once against the fetched list) - never the free-text
  // ROI/vendor/request-id search fields, which identify a specific request
  // rather than a reusable UI preference.
  const [persistedHealthSystem, setPersistedHealthSystem] = usePersistedState(
    'requestDetails:healthSystem',
    '',
    isString,
  );
  const setSelectedHealthSystem = (value: HealthSystemOption | null) => {
    setSelectedHealthSystemState(value);
    setPersistedHealthSystem(value?.value ?? '');
  };
  const [rowsPerPage, setRowsPerPage] = usePersistedState(
    'requestDetails:rowsPerPage',
    DEFAULT_ROWS_PER_PAGE,
    isNumberIn(REQUEST_ROWS_PER_PAGE_OPTIONS),
  );

  const [jobSummaryRows, setJobSummaryRows] = useState<JobSummaryRow[]>([]);
  const [isJobSummaryOpen, setIsJobSummaryOpen] = useState(false);
  const [jobSummaryPage, setJobSummaryPage] = useState(0);
  const [jobSummaryRowsPerPage, setJobSummaryRowsPerPage] = useState(
    DEFAULT_JOB_SUMMARY_ROWS_PER_PAGE,
  );

  /** Fetches one page of requests for the given health system and filters. */
  const loadRequests = useCallback(
    async (
      vendorid: string | null,
      fields: FilterField[] | null,
      nextPage: number,
      nextRowsPerPage: number,
    ) => {
      setIsGridLoading(true);
      setRequests([]);

      const response = await fetchAllRequests({
        vendorid,
        searchFields: fields,
        page: nextPage,
        rowsPerPage: nextRowsPerPage,
      });

      setRequests(response.requestList);
      setTotalRequestCount(response.totalRequests);
      setIsGridLoading(false);
    },
    [],
  );

  /**
   * Reloads both the donut chart and the (unfiltered) first page of requests.
   *
   * `nextRowsPerPage` defaults to the fixed default everywhere except the
   * initial mount, which passes the persisted page size instead - otherwise
   * this reset would immediately clobber it back to 100 on every reload.
   */
  const loadHealthSystem = useCallback(
    async (
      vendorid: string | null,
      fields: FilterField[] | null,
      nextRowsPerPage: number = DEFAULT_ROWS_PER_PAGE,
    ) => {
      setPage(0);
      setRowsPerPage(nextRowsPerPage);
      setTotalRequestCount(null);

      const counts = await fetchHealthSystemSummaryCount(vendorid);
      setStatusCounts(counts);
      setIsLoading(false);

      await loadRequests(vendorid, fields, 0, nextRowsPerPage);
    },
    [loadRequests, setRowsPerPage],
  );

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const [systems, options] = await Promise.all([
        fetchAllHealthSystems(),
        fetchAllPurposeOfUses(),
      ]);
      if (cancelled) return;

      const defaultSystem =
        systems.find((system) => system.value === persistedHealthSystem) ??
        systems[0] ??
        null;
      const fields = buildSearchFields(options);

      setHealthSystems(systems);
      setSelectedHealthSystem(defaultSystem);
      setPurposeOfUses(options);
      setSearchFields(fields);

      await loadHealthSystem(defaultSystem?.value ?? null, fields, rowsPerPage);
    };

    void init();
    return () => {
      cancelled = true;
    };
    // Runs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHealthSystemChange = (value: HealthSystemOption) => {
    const fields = buildSearchFields(purposeOfUses);
    setSelectedHealthSystem(value);
    setSearchFields(fields);
    void loadHealthSystem(value.value, fields);
  };

  const handleRefresh = () => {
    const fields = buildSearchFields(purposeOfUses);
    setSearchFields(fields);
    void loadHealthSystem(selectedHealthSystem?.value ?? null, fields);
  };

  /**
   * Updates one search field and re-validates the from/to pair, since either
   * date changing can clear or introduce an error on the other.
   */
  const handleFieldChange = (
    responseValue: string,
    value: string | Option | null,
  ) => {
    setSearchFields((previous) => {
      const next = previous.map((field) =>
        field.responseValue === responseValue ? { ...field, value } : field,
      );

      const fromValue =
        (next.find((f) => f.responseValue === 'fromdate')?.value as string) ??
        '';
      const toValue =
        (next.find((f) => f.responseValue === 'todate')?.value as string) ?? '';
      const errors = validateDateRange(fromValue, toValue);

      return next.map((field) => {
        if (field.responseValue === 'fromdate') {
          return { ...field, ...errors.fromdate };
        }
        if (field.responseValue === 'todate') {
          return { ...field, ...errors.todate };
        }
        return field;
      });
    });
  };

  const handleSearch = () => {
    if (searchFields.some((field) => field.isError)) return;

    setPage(0);
    setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
    void loadRequests(
      selectedHealthSystem?.value ?? null,
      searchFields,
      0,
      DEFAULT_ROWS_PER_PAGE,
    );
  };

  const handleClearSearch = () => {
    const fields = buildSearchFields(purposeOfUses);
    setSearchFields(fields);
    setPage(0);
    setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
    void loadRequests(
      selectedHealthSystem?.value ?? null,
      null,
      0,
      DEFAULT_ROWS_PER_PAGE,
    );
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    void loadRequests(
      selectedHealthSystem?.value ?? null,
      searchFields,
      nextPage,
      rowsPerPage,
    );
  };

  const handleRowsPerPageChange = (nextRowsPerPage: number) => {
    setRowsPerPage(nextRowsPerPage);
    setPage(0);
    void loadRequests(
      selectedHealthSystem?.value ?? null,
      searchFields,
      0,
      nextRowsPerPage,
    );
  };

  /** Loads job-summary rows for one request, showing a spinner on that row. */
  const handleViewJobSummary = async (row: RequestRow) => {
    const setRowLoader = (loading: boolean) =>
      setRequests((previous) =>
        previous.map((request) =>
          request.id === row.id
            ? { ...request, showJobSummaryLoader: loading }
            : request,
        ),
      );

    setRowLoader(true);
    setJobSummaryPage(0);
    setJobSummaryRowsPerPage(DEFAULT_JOB_SUMMARY_ROWS_PER_PAGE);

    const rows = await fetchJobSummary(row.requestid, row.requesterrefid);

    setRowLoader(false);
    setJobSummaryRows(rows);
    setIsJobSummaryOpen(true);
  };

  return (
    <Container className='main-container'>
      {isLoading ? <Loader fullHeight /> : null}

      <DialogModal
        isOpen={isJobSummaryOpen}
        modalTitle='Job Summary Details'
        maxWidth='lg'
        showCloseIcon
        onClose={() => setIsJobSummaryOpen(false)}
        modalCancelButtonText='Cancel'
        modalContent={
          <JobSummaryTable
            rows={jobSummaryRows}
            page={jobSummaryPage}
            rowsPerPage={jobSummaryRowsPerPage}
            onPageChange={setJobSummaryPage}
            onRowsPerPageChange={(value) => {
              setJobSummaryRowsPerPage(value);
              setJobSummaryPage(0);
            }}
          />
        }
      />

      <Grid container className='all-resouces-list-wrapper' spacing={3}>
        <Grid size={12}>
          <Paper className='respurce-list-paper'>
            <Grid container spacing={2}>
              <Grid size={{ xs: 8, md: 4 }}>
                <Autocomplete
                  options={healthSystems}
                  value={selectedHealthSystem as HealthSystemOption}
                  size='small'
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  disableClearable
                  blurOnSelect
                  onChange={(_event, value) =>
                    value && handleHealthSystemChange(value)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label='Select Health System' />
                  )}
                  className='autocomplete-field'
                />
              </Grid>

              <Grid
                size={{ xs: 4, md: 8 }}
                sx={{ display: 'flex', justifyContent: 'flex-end' }}
              >
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleRefresh}
                  className='show-req-btn'
                  title='Click to refresh data'
                  disabled={isGridLoading}
                >
                  Refresh Chart
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid size={12}>
          <Paper sx={{ padding: 3 }}>
            <StatusDonutChart data={statusCounts} height={480} />
          </Paper>
        </Grid>

        {!isLoading ? (
          <Grid size={12}>
            <Grid container spacing={3} className='request-container'>
              <Grid size={12}>
                <Paper
                  className='category-paper-wrapper'
                  sx={{ width: '100%' }}
                >
                  <RequestFilterBar
                    fields={searchFields}
                    isLoading={isGridLoading}
                    onFieldChange={handleFieldChange}
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                  />

                  <RequestsTable
                    rows={requests}
                    totalCount={totalRequestCount}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    isLoading={isGridLoading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    onViewJobSummary={handleViewJobSummary}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        ) : null}
      </Grid>
    </Container>
  );
};

export default Dashboard;
